<template>
  <div id="dashboard">
    <div class="page-title px-4">
      <h2>
        <span class="me-2">{{ appName }} Dashboard</span>
      </h2>
    </div>

    <v-divider class="mt-4 mb-10"></v-divider>

    <v-row>
      <v-col cols="12" md="6">
        <v-card class="greeting-card pb-2">
          <v-row class="ma-0 pa-0">
            <v-col v-if="$auth.isAuthenticated" cols="12">
              <v-card-title class="text-no-wrap pt-1 ps-2">
                <v-badge bottom color="success" overlap offset-x="12" offset-y="12" class="mr-4" dot>
                  <v-avatar size="40px" color="primary" class="v-avatar-light-bg primary--text">
                    <v-img :src="$auth.avatar"></v-img>
                  </v-avatar>
                </v-badge>
                <div class="d-flex flex-column">
                  <span>Welcome back, {{ $auth.user.name }}</span>
                  <span v-if="$auth.user.organisation" class="text-caption text--secondary">
                    Organisation: {{ $auth.user.organisation }}
                  </span>
                </div>
              </v-card-title>

              <!-- Quick Vehicle Search Widget -->
              <v-card-title class="mt-4 px-2">
                <v-icon color="primary" class="me-2">{{ icons.mdiBus }}</v-icon>
                Quick Vehicle Lookup
              </v-card-title>
              <v-card-text>
                <v-form @submit.prevent="goToVehicle">
                  <v-row align="center" no-gutters>
                    <v-col cols="8" sm="9">
                      <v-text-field
                        v-model="quickVehicleId"
                        label="Enter Vehicle ID"
                        placeholder="e.g. 1234"
                        outlined
                        dense
                        hide-details
                        class="me-2"
                        @keyup.enter="goToVehicle"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4" sm="3">
                      <v-btn color="primary" block :disabled="!quickVehicleId.trim()" @click="goToVehicle">
                        <v-icon left>{{ icons.mdiArrowRight }}</v-icon>
                        Go
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="">
              <v-row class="ma-0 pa-0">
                <v-col cols="12">
                  <p class="text-center text-no-wrap pt-1 text--xs">Total Devices</p>
                  <v-card-text class="text-center pb-2 ps-2 text-xl font-weight-semibold text--primary">
                    <div>
                      {{ allDevices.length }}
                    </div>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="">
              <v-row class="ma-0 pa-0">
                <v-col cols="12">
                  <p class="text-center text-no-wrap pt-1 text--xs">Enabled Vehicles</p>
                  <v-card-text class="text-center pb-2 ps-2 text-xl font-weight-semibold text--primary">
                    <div>{{ enabledVehicles.length }} / {{ validVehicles.length }}</div>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="">
              <v-row class="ma-0 pa-0">
                <v-col cols="12">
                  <p class="text-center text-no-wrap pt-1 text--xs">Vehicles On Trip</p>
                  <v-card-text class="text-center pb-2 ps-2 text-xl font-weight-semibold text--primary">
                    <div>
                      {{ onTripVehicles.length }}
                    </div>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="">
              <v-row class="ma-0 pa-0">
                <v-col cols="12">
                  <p class="text-center text-no-wrap pt-1 text--xs">Open Notes</p>
                  <v-card-text class="text-center pb-2 ps-2 text-xl font-weight-semibold text--primary">
                    <div>
                      {{ openNotes.length }}
                    </div>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="3">
        <v-card class="">
          <v-row class="ma-0 pa-0">
            <v-col cols="12">
              <p class="text-center text-no-wrap pt-1 text--xs">Vehicles per Operator</p>

              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Operator</th>
                      <th class="text-left">Vehicles</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="[operator, vehicles] in Object.entries(vehiclesByOperator)" :key="operator">
                      <td>
                        <router-link
                          :to="{
                            name: 'next-stop-vehicles',
                            query: { sqOperator: operator },
                          }"
                          class="cursor-pointer text-decoration-none"
                        >
                          {{ operator }}
                        </router-link>
                      </td>
                      <td>{{ vehicles.length }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="">
          <v-row class="ma-0 pa-0">
            <v-col cols="12">
              <p class="text-center text-no-wrap pt-1 text--xs">Open Notes by Assignment</p>

              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Organisation</th>
                      <th class="text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(group, normalizedName) in notesbyAssignment" :key="normalizedName">
                      <td>
                        <router-link
                          :to="{
                            name: 'next-stop-notes',
                            query: { sqAssigned: group.displayAssignedTo, sqStatus: noteOpenStatuses },
                          }"
                          class="cursor-pointer text-decoration-none"
                        >
                          {{ group.displayAssignedTo }}
                        </router-link>
                      </td>
                      <td>{{ group.notes.length }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="">
          <v-row class="ma-0 pa-0">
            <v-col cols="12">
              <p class="text-center text-no-wrap pt-1 text--xs">Software Versions (Vehicles)</p>

              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Software</th>
                      <th class="text-left">Vehicles</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="[releaseVersion, devices] in Object.entries(vehiclesByReleaseVersion)"
                      :key="releaseVersion"
                    >
                      <td>{{ releaseVersion }}</td>
                      <td>{{ devices.length }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card class="">
          <v-row class="ma-0 pa-0">
            <v-col cols="12">
              <p class="text-center text-no-wrap pt-1 text--xs">Vehicles by Status</p>

              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Status</th>
                      <th class="text-left">Vehicles</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="[status, vehicles] in Object.entries(vehiclesByStatus)" :key="status">
                      <td>
                        <router-link
                          :to="{
                            name: 'next-stop-vehicles',
                            query: { sqDStatus: status },
                          }"
                          class="cursor-pointer text-decoration-none"
                        >
                          {{ status }}
                        </router-link>
                      </td>
                      <td>{{ vehicles.length }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mdiLogoutVariant, mdiViewDashboard, mdiAccountOutline, mdiEmailBox, mdiAccountCowboyHat, mdiBus, mdiArrowRight } from "@mdi/js"
import themeConfig from "@themeConfig"
import { mapState } from "vuex"

export default {
  metaInfo() {
    return {
      title: "Dashboard",
    }
  },

  setup() {
    return {
      // themeConfig
      appName: themeConfig.app.name,
      appLogo: themeConfig.app.logo,

      icons: {
        mdiLogoutVariant,
        mdiViewDashboard,
        mdiAccountOutline,
        mdiEmailBox,
        mdiAccountCowboyHat,
        mdiBus,
        mdiArrowRight,
      },
    }
  },

  data() {
    return {
      noteOpenStatuses: ["Open", "Pending", "On Hold"],
      quickVehicleId: "",
    }
  },

  computed: {
    ...mapState({
      vehicles: state => state.gtfs.vehicles,
      devices: state => state.gtfs.devices,
      notes: state => state.gtfs.notes,
    }),
    allDevices() {
      return this.devices
    },
    openNotes() {
      return this.notes.filter(notice => notice.status === "Open")
    },
    enabledVehicles() {
      return this.vehicles.filter(vehicle => vehicle.config.is_enabled === true && vehicle.config.operator !== "Test")
    },
    validVehicles() {
      return this.vehicles.filter(vehicle => vehicle.config.operator !== "Test")
    },
    onTripVehicles() {
      return this.vehicles.filter(vehicle => vehicle.config.is_enabled && vehicle.status.trip.trip_id !== null)
    },
    vehiclesByOperator() {
      return this.vehicles.reduce((acc, vehicle) => {
        const operator = vehicle.config.operator || "-"
        if (!acc[operator]) {
          acc[operator] = []
        }
        acc[operator].push(vehicle)

        return acc
      }, {})
    },
    vehiclesByOSVersion() {
      return this.devices.reduce((acc, device) => {
        // Exclude devices without an assigned vehicle_id
        if (!device.config.vehicle_id) {
          return acc
        }
        const osVersion = device.status.os_version || "Unknown"
        if (!acc[osVersion]) {
          acc[osVersion] = []
        }
        acc[osVersion].push(device)

        return acc
      }, {})
    },
    vehiclesByReleaseVersion() {
      return this.devices.reduce((acc, device) => {
        // Exclude devices without an assigned vehicle_id
        if (!device.config.vehicle_id) {
          return acc
        }
        const releaseVersion = device.status.release_version || "Unknown"
        if (!acc[releaseVersion]) {
          acc[releaseVersion] = []
        }
        acc[releaseVersion].push(device)

        return acc
      }, {})
    },
    vehiclesByStatus() {
      return this.vehicles.reduce((acc, vehicle) => {
        const status = vehicle.status.status || "Unknown"
        if (!acc[status]) {
          acc[status] = []
        }
        acc[status].push(vehicle)

        return acc
      }, {})
    },
    notesbyAssignment() {
      return this.notes.reduce((data, note) => {
        if (note.status !== "Closed") {
          const rawAssignedTo = note.assigned || "-"
          const assigned_to_normalized = rawAssignedTo.toLowerCase()

          if (!data[assigned_to_normalized]) {
            data[assigned_to_normalized] = {
              displayAssignedTo: rawAssignedTo,
              notes: [],
            }
          }

          data[assigned_to_normalized].notes.push(note)
        }

        return data
      }, {})
    },
  },

  methods: {
    // Log the user out
    logout() {
      this.$auth.logout({
        returnTo: window.location.origin,
      })
    },
    // Navigate to vehicle configuration page
    goToVehicle() {
      const vehicleId = this.quickVehicleId.trim()
      if (vehicleId) {
        this.$router.push({ name: "next-stop-vehicle-config", params: { id: vehicleId } })
      }
    },
  },
}
</script>
